import React from 'react';
import {
  BadgeCheck,
  Gift,
  Headphones,
  LockKeyhole,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  Ticket,
  UsersRound
} from 'lucide-react';
import Header from './Header';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { PEEKPASS_BRAND } from '../utils/peekPassBrand';

export type InfoPageKey = 'how-it-works' | 'policies' | 'privacy' | 'terms' | 'support' | 'club';

interface InfoViewProps {
  page: InfoPageKey;
  onGoBack: () => void;
}

interface InfoSection {
  title: string;
  description: string;
  points: string[];
}

interface InfoContent {
  eyebrow: string;
  title: string;
  lead: string;
  sections: InfoSection[];
}

const content: Record<InfoPageKey, InfoContent> = {
  'how-it-works': {
    eyebrow: 'Comprar en PEEK PASS',
    title: 'Cómo funciona',
    lead: 'Una forma directa de encontrar una fecha, elegir tu acceso y recibir tu compra por el flujo de pago disponible.',
    sections: [
      { title: '1. Descubrí una fecha', description: 'Usá la agenda, los filtros o la búsqueda para encontrar la experiencia que querés vivir.', points: ['Explorá por categoría, mes y tipo de acceso', 'Revisá location, fecha, precio y disponibilidad', 'Guardá tus favoritos para volver después'] },
      { title: '2. Elegí tu acceso', description: 'Cada evento muestra sus alternativas de entrada para que compares antes de continuar.', points: ['General para vivir la fecha', 'Preferencial para mejores ubicaciones', 'Premium o VIP para propuestas especiales'] },
      { title: '3. Confirmá tu compra', description: 'Sumá tus entradas a la reserva y seguí el checkout seguro para completar el pago.', points: ['Resumen claro antes de pagar', 'Confirmación al finalizar la operación', 'Acceso a tus reservas desde tu cuenta'] }
    ]
  },
  policies: {
    eyebrow: 'Información para asistentes',
    title: 'Políticas de uso',
    lead: 'Estas pautas ordenan la compra, el acceso a los eventos y la relación entre asistentes, organizadores y la plataforma.',
    sections: [
      { title: 'Compra y reserva', description: 'Revisá la información de la fecha antes de confirmar una reserva.', points: ['Los precios y condiciones se informan antes del checkout', 'Cada reserva está sujeta a disponibilidad', 'La confirmación de pago acredita la operación'] },
      { title: 'Acceso al evento', description: 'La entrada es válida para el evento, fecha y tipo de acceso seleccionado.', points: ['Presentá el comprobante o entrada solicitada por la producción', 'Llegá con anticipación al horario informado', 'Respetá las normas de la location y de la organización'] },
      { title: 'Cambios y soporte', description: 'Las modificaciones dependen de las condiciones comunicadas por cada organizador.', points: ['Consultá la política particular de cada evento', 'Informá cualquier inconveniente con tiempo suficiente', 'El equipo de soporte orienta sobre la gestión disponible'] }
    ]
  },
  privacy: {
    eyebrow: 'Tu información',
    title: 'Privacidad',
    lead: 'Cuidamos los datos necesarios para operar tu cuenta, procesar compras y darte soporte cuando lo necesitás.',
    sections: [
      { title: 'Datos que usamos', description: 'Usamos la información imprescindible para que tu cuenta y tus reservas funcionen correctamente.', points: ['Datos de contacto y acceso a la cuenta', 'Información de reservas y operaciones', 'Preferencias que elegís dentro de la plataforma'] },
      { title: 'Para qué los usamos', description: 'Los datos nos permiten confirmar compras, resolver consultas y mejorar la experiencia.', points: ['Enviar confirmaciones y novedades relevantes', 'Brindar asistencia sobre una reserva', 'Prevenir usos indebidos de la cuenta'] },
      { title: 'Tus decisiones', description: 'Podés solicitar ayuda sobre tu información desde los canales de soporte.', points: ['Revisá tus datos desde tu perfil', 'Consultá sobre una reserva o compra', 'Escribinos si necesitás orientación adicional'] }
    ]
  },
  terms: {
    eyebrow: 'Marco de uso',
    title: 'Términos y condiciones',
    lead: 'Al usar PEEK PASS aceptás estas reglas básicas de navegación, compra y publicación de eventos.',
    sections: [
      { title: 'Uso de la plataforma', description: 'La cuenta es personal y debe utilizarse con información correcta y actualizada.', points: ['Mantené protegidos tus datos de acceso', 'No uses la plataforma para actividades no autorizadas', 'Respetá los derechos de terceros y de los organizadores'] },
      { title: 'Eventos publicados', description: 'La producción es responsable de la información específica de cada fecha.', points: ['Verificá datos, precios y condiciones antes de publicar', 'Comunicá cambios relevantes a los asistentes', 'Cumplí las normas aplicables en tu jurisdicción'] },
      { title: 'Actualizaciones', description: 'Podemos ajustar estas condiciones para reflejar mejoras operativas o cambios del servicio.', points: ['La versión vigente se muestra en esta vista', 'Las condiciones de cada evento se informan antes de comprar', 'El soporte puede orientar ante dudas concretas'] }
    ]
  },
  support: {
    eyebrow: 'PEEK PASS CLUB',
    title: 'Soporte y contacto',
    lead: 'Un equipo humano para orientar compras, reservas, accesos y consultas de productoras.',
    sections: [
      { title: 'Asistencia para asistentes', description: 'Te ayudamos a entender el estado de una reserva o los pasos disponibles para tu evento.', points: ['Compra, reserva y confirmación', 'Datos del evento y condiciones informadas', 'Orientación antes y después de la fecha'] },
      { title: 'Asistencia para productoras', description: 'Acompañamos a los equipos que quieren publicar una fecha y ordenar su venta de entradas.', points: ['Carga de evento y accesos', 'Información para publicación', 'Recorrido de venta y cobro disponible'] },
      { title: 'Canal de contacto', description: 'Escribinos a support@peekpass.club con el nombre del evento y el detalle de tu consulta.', points: ['Atención humana para compradores y producciones', 'Sumá tu número de reserva cuando lo tengas', 'Te respondemos por el mismo canal de contacto'] }
    ]
  },
  club: {
    eyebrow: 'Beneficios para volver',
    title: 'PEEK PASS CLUB',
    lead: 'El club de beneficios para quienes viven más fechas: puntos, prioridad en preventas y propuestas pensadas para volver a salir.',
    sections: [
      { title: 'Sumá puntos por experiencias', description: 'Cada compra confirmada puede acercarte a beneficios dentro de PEEK PASS CLUB.', points: ['Puntos por entradas y preventas seleccionadas', 'Bonificaciones en campañas del club', 'Historial de actividad desde tu cuenta'] },
      { title: 'Accedé antes', description: 'Las promociones del club se organizan para que descubras fechas y accesos con tiempo.', points: ['Avisos de preventa y lanzamientos', 'Promociones especiales por categoría', 'Invitaciones a experiencias seleccionadas'] },
      { title: 'Viví más de cerca', description: 'El club busca reconocer a quienes hacen de los eventos una parte de su agenda.', points: ['Beneficios comunicados de forma clara', 'Condiciones visibles antes de activar cada promoción', 'Nuevas ventajas a medida que crece la comunidad'] }
    ]
  }
};

const icons = [Ticket, BadgeCheck, ReceiptText, ShieldCheck, LockKeyhole, Headphones, UsersRound, Gift, Sparkles];

const InfoView: React.FC<InfoViewProps> = ({ page, onGoBack }) => {
  const pageContent = content[page];
  useScrollToTop();

  return (
    <div className="min-h-screen bg-[#f8f5ef] text-slate-950">
      <Header showBackButton onBack={onGoBack} title={pageContent.title} />
      <main className="px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <section className="mx-auto max-w-6xl">
          <div className="overflow-hidden rounded-[2rem] bg-black px-6 py-10 text-white shadow-[0_32px_90px_-45px_rgba(15,23,42,0.6)] sm:px-10 sm:py-14">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#dff38c]">{pageContent.eyebrow}</p>
            <h1 className="mt-4 max-w-3xl text-4xl font-black tracking-[-0.06em] sm:text-5xl">{pageContent.title}</h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-white/70 sm:text-lg">{pageContent.lead}</p>
          </div>

          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {pageContent.sections.map((section, index) => {
              const Icon = icons[index % icons.length];
              return (
                <article key={section.title} className="rounded-[1.7rem] border border-slate-200 bg-white p-6 shadow-[0_22px_60px_-42px_rgba(15,23,42,0.32)]">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#e9f7b7] text-slate-950"><Icon className="h-5 w-5" /></span>
                  <h2 className="mt-6 text-xl font-black tracking-[-0.04em]">{section.title}</h2>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{section.description}</p>
                  <ul className="mt-5 space-y-3 text-sm leading-6 text-slate-700">
                    {section.points.map((point) => <li key={point} className="flex gap-2"><BadgeCheck className="mt-1 h-4 w-4 shrink-0 text-slate-950" />{point}</li>)}
                  </ul>
                </article>
              );
            })}
          </div>

          <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-[1.7rem] border border-slate-200 bg-white px-6 py-5 text-center sm:flex-row sm:text-left">
            <div>
              <p className="font-black">{PEEKPASS_BRAND}</p>
              <p className="mt-1 text-sm text-slate-600">Eventos para descubrir, reservar y vivir.</p>
            </div>
            <button type="button" onClick={onGoBack} className="rounded-full bg-black px-5 py-3 text-sm font-bold text-white">Volver a la agenda</button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default InfoView;
